import User from 'account/dto/User'
import ConfirmModal from 'components/modal/ConfirmModal'
import { omit } from 'lodash'
import moment from 'moment'
import PropTypes from 'prop-types'
import React, { useMemo, useState } from 'react'
import { connect, shallowEqual, useSelector } from 'react-redux'
import MultiContributorsAutocomplete from 'referencial/components/contributor/components/MultiContributorsAutocomplete'
import i18n from 'simple-react-i18n'
import { authorizeContributors, authorizeFilter } from 'utils/HabilitationUtil'
import { removeNullKeys } from 'utils/StoreUtils'
import Input from '../../../../components/forms/Input'
import NumberField from '../../../../components/forms/NumberField'
import Select from '../../../../components/forms/Select'
import Textarea from '../../../../components/forms/Textarea'
import ContributorDto from '../../../../referencial/components/contributor/dto/ContributorDto'
import NetworkDto from '../../../../referencial/components/network/dto/NetworkDto'
import { getMatTypes } from '../../../../utils/MaterielUtils'
import PowerSupplyDto from '../dto/PowerSupplyDto'
import PowerSupplyTypeDto from '../dto/PowerSupplyTypeDto'
import { POWER_SUPPLY_ID } from 'materiel/constants/MaterielConstants'
import { isFieldRequired } from 'materiel/utils/MaterielUtils'
import { DATEACHAT_OBLIGATOIRE, NUMSERIE_OBLIGATOIRE } from 'materiel/constants/MaterielSettingRuleConstant'
import SimpleDatePicker from 'components/forms/SimpleDatePicker'
import { Grid, Stack } from '@mui/material'

const PowerSupplyPanel = ({
    active = false,
    disabled = true,
    powerSupply = {},
    contributors = [],
    accountUser = {},
    networks = [],
    powerSupplyTypes = [],
    onChange = () => { },
}) => {
    const {
        materielSettingRules,
    } = useSelector(store => ({
        materielSettingRules: store.MaterielReducer.materielSettingRules,
    }), shallowEqual)

    const [isOpen, setOpen] = useState(false)
    const [selectedType, setSelectedType] = useState()

    const rules = useMemo(() => {
        return materielSettingRules.filter(({ idMaterielType }) => idMaterielType === POWER_SUPPLY_ID)
    }, [materielSettingRules])

    return (
        <Stack columnGap={2}>
            <Grid container columnSpacing={2} alignItems='center'>
                <Grid size={4}>
                    <Select
                        options={getMatTypes(powerSupplyTypes, powerSupply.powerSupplyType)}
                        label={i18n.type}
                        onChange={v => {
                            const type = powerSupplyTypes.find(({ id }) => v === id)
                            if (type) {
                                setOpen(true)
                                setSelectedType(type)
                            } else {
                                onChange({ powerSupplyType: v })
                            }
                        }}
                        value={powerSupply.powerSupplyType}
                        nullLabel='&nbsp;'
                        acive={active}
                        disabled={disabled}
                        obligatory
                    />
                </Grid>
                <Grid size={4}>
                    <Input
                        title={i18n.serialNumber}
                        value={powerSupply.serialNumber}
                        onChange={value => onChange({ serialNumber: value })}
                        acive={active}
                        disabled={powerSupply.isEquipment || disabled}
                        obligatory={isFieldRequired(rules, NUMSERIE_OBLIGATOIRE)}
                    />
                </Grid>
                <Grid size={4}>
                    <Input
                        title={i18n.immoNumber}
                        value={powerSupply.reference}
                        onChange={value => onChange({ reference: value })}
                        acive={active}
                        disabled={powerSupply.isEquipment || disabled}
                    />
                </Grid>
            </Grid>
            <Grid container columnSpacing={2} alignItems='center'>
                <Grid size={6}>
                    <MultiContributorsAutocomplete
                        options={authorizeContributors(contributors, accountUser, powerSupply.manufacturerId)}
                        label={i18n.manufacturer}
                        onChange={(id) => onChange({ manufacturerId: id })}
                        values={powerSupply.manufacturerId}
                        disabled={powerSupply.isEquipment || disabled}
                        keyLabel='labelDisplay'
                    />
                </Grid>
                <Grid size={6}>
                    <MultiContributorsAutocomplete
                        options={authorizeContributors(contributors, accountUser, powerSupply.providerId)}
                        label={i18n.provider}
                        onChange={(id) => onChange({ providerId: id })}
                        values={powerSupply.providerId}
                        disabled={powerSupply.isEquipment || disabled}
                        keyLabel='labelDisplay'
                    />
                </Grid>
            </Grid>
            <Select
                options={authorizeFilter(networks, accountUser, powerSupply.networkCode)}
                label={i18n.network}
                onChange={value => onChange({ networkCode: value })}
                value={powerSupply.networkCode}
                nullLabel='&nbsp;'
                acive={active}
                disabled={powerSupply.isEquipment || disabled}
            />
            <Grid container columnSpacing={2} alignItems='center'>
                <Grid size={3}>
                    <NumberField
                        title={`${i18n.voltage} (V)`}
                        value={powerSupply.voltage}
                        onChange={v => onChange({ voltage: v })}
                        floatValue
                        acive={active}
                        disabled={disabled}
                    />
                </Grid>
                <Grid size={3}>
                    <NumberField
                        title={`${i18n.lifeTime} (${i18n.month})`}
                        value={powerSupply.lifeTime}
                        onChange={v => onChange({ lifeTime: v })}
                        floatValue
                        acive={active}
                        disabled={disabled}
                    />
                </Grid>
                <Grid size={3}>
                    <NumberField
                        title={`${i18n.power} (W)`}
                        value={powerSupply.power}
                        onChange={v => onChange({ power: v })}
                        floatValue
                        active={active}
                        disabled={disabled}
                    />
                </Grid>
                <Grid size={3}>
                    <NumberField
                        title={`${i18n.batteryCapacity} (Ah)`}
                        value={powerSupply.capacity}
                        onChange={v => onChange({ capacity: v })}
                        floatValue
                        active={active}
                        disabled={disabled}
                    />
                </Grid>
            </Grid>
            <Grid container columnSpacing={2} alignItems='center'>
                <Grid size={3}>
                    <NumberField
                        title={`${i18n.weight} (kg)`}
                        value={powerSupply.weight}
                        onChange={v => onChange({ weight: v })}
                        floatValue
                        active={active}
                        disabled={disabled}
                    />
                </Grid>
                <Grid size={3}>
                    <Input
                        title={i18n.technology}
                        value={powerSupply.technology}
                        onChange={value => onChange({ technology: value })}
                        active={active}
                        disabled={disabled}
                    />
                </Grid>
                <Grid size={3}>
                    <Input
                        title={i18n.exposure}
                        value={powerSupply.exposure}
                        onChange={value => onChange({ exposure: value })}
                        active={active}
                        disabled={disabled}
                    />
                </Grid>
                <Grid size={3}>
                    <Input
                        title={i18n.dimension}
                        value={powerSupply.dimension}
                        onChange={value => onChange({ dimension: value })}
                        active={active}
                        disabled={disabled}
                    />
                </Grid>
            </Grid>
            <Grid container columnSpacing={2} alignItems='center'>
                <Grid size={12}>
                    <Input
                        title={i18n.locationSituation}
                        value={powerSupply.locationSituation}
                        onChange={value => onChange({ locationSituation: value })}
                        active={active}
                        disabled={disabled}
                    />
                </Grid>
            </Grid>
            <Grid container columnSpacing={2} alignItems='center'>
                <Grid size={3}>
                    <SimpleDatePicker
                        id='manufactureDate'
                        label={i18n.manufactureDate}
                        value={powerSupply.manufactureDate}
                        onChange={v => onChange({ manufactureDate: v })}
                        disabled={powerSupply.isEquipment || disabled}
                        max={powerSupply.purchaseDate}
                    />
                </Grid>
                <Grid size={3}>
                    <SimpleDatePicker
                        id='purchaseDate'
                        label={i18n.purchaseDate}
                        value={powerSupply.purchaseDate}
                        onChange={v => onChange({ purchaseDate: v })}
                        disabled={powerSupply.isEquipment || disabled}
                        min={powerSupply.manufactureDate}
                        obligatory={isFieldRequired(rules, DATEACHAT_OBLIGATOIRE)}
                    />
                </Grid>
                <Grid size={3}>
                    <SimpleDatePicker
                        id='withdrawalDate'
                        label={i18n.withdrawalDate}
                        value={powerSupply.withdrawalDate}
                        onChange={v => onChange({ withdrawalDate: v })}
                        disabled={powerSupply.isEquipment || disabled}
                        min={powerSupply.purchaseDate}
                    />
                </Grid>
                <Grid size={3}>
                    <SimpleDatePicker
                        label={i18n.warrantyExpirationDate}
                        value={powerSupply.warrantyTime && moment(powerSupply.purchaseDate).add(powerSupply.warrantyTime, 'months').valueOf()}
                        disabled
                    />
                </Grid>
            </Grid>
            <Textarea
                title={i18n.comment}
                value={powerSupply.comment}
                onChange={value => onChange({ comment: value })}
                acive={active}
                disabled={disabled}
            />
            <ConfirmModal
                isOpen={isOpen}
                title={i18n.materielDefaultValuesMessage}
                onValidate={() => {
                    setOpen(false)
                    onChange(removeNullKeys({
                        powerSupplyType: selectedType.id,
                        ...omit(selectedType, ['id', 'code', 'label', 'name', 'comment', 'updateDate', 'updateLogin', 'startDate', 'endDate', 'icon']),
                    }))
                }}
                onReject={() => {
                    setOpen(false)
                    onChange({ powerSupplyType: selectedType.id })
                }}
                onClose={() => setOpen(false)}
            />
        </Stack>
    )
}


PowerSupplyPanel.propTypes = {
    disabled: PropTypes.bool,
    active: PropTypes.bool,
    onChange: PropTypes.func,
    powerSupply: PropTypes.instanceOf(PowerSupplyDto),
    powerSupplyTypes: PropTypes.arrayOf(PropTypes.instanceOf(PowerSupplyTypeDto)),
    contributors: PropTypes.arrayOf(PropTypes.instanceOf(ContributorDto)),
    networks: PropTypes.arrayOf(PropTypes.instanceOf(NetworkDto)),
    accountUser: PropTypes.instanceOf(User),
}

const mapStateToProps = store => ({
    contributors: store.ContributorReducer.contributors,
    powerSupplyTypes: store.PowerSupplyReducer.powerSupplyTypes,
    networks: store.NetworkReducer.networks,
    accountUser: store.AccountReducer.accountUser,
})

export default connect(mapStateToProps)(PowerSupplyPanel)
